import { memo, useCallback, useEffect, useMemo } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Controller, useForm } from "react-hook-form";

import type { UseCrmSetupConfigurationResult } from "../../setup/hooks/useCrmSetupConfiguration";
import type { CrmSetupConfigurationOption } from "../../setup/types";
import { useWhatsAppEndpointContacts } from "../hooks/useWhatsAppEndpointContacts";
import WhatsAppConfigurationBillingActions from "./WhatsAppConfigurationBillingActions";
import { messagingConstants } from "../../messaging";

export interface WhatsAppEndpointContactsPanelProps {
  organizationId?: string;
  wabaId?: string;
  /** Canal escolhido no setup CRM (define rótulos/fluxos de billing). */
  whatsappChannelOption?: CrmSetupConfigurationOption | null;
  /** Obrigatório para o dialog de canal/contrato (evita estado duplicado). */
  setupConfiguration: UseCrmSetupConfigurationResult;
  /** Extensão FluxZap assinada — necessária para vincular número Meta. */
  hasFluxZapExtensionActive?: boolean;
  isFluxZapExtensionLoading?: boolean;
  /** Recarrega status da extensão após assinatura. */
  onFluxZapExtensionActivated?: () => void;
  /** Signal para refresh de estatísticas após envio de mensagem ou compra de pacote. */
  franchiseStatsRefreshSignal?: number;
  /**
   * Callback executado após checkout Fluxmed completado.
   */
  onFluxmedCheckoutCompleted?: () => void;
  hasConfiguredAutomations?: boolean;
  removeAllAutomationConfigs?: () => Promise<void>;
  persistedWabaId?: string;
  onAfterChannelChange?: () => void | Promise<void>;
}

function reorderContactsBySelectedIndex(
  contacts: fhir5.ContactPoint[],
  selectedIndex: number,
): fhir5.ContactPoint[] {
  if (selectedIndex <= 0) return contacts;
  if (selectedIndex >= contacts.length) return contacts;
  const nextContacts = [...contacts];
  const [moved] = nextContacts.splice(selectedIndex, 1);
  nextContacts.unshift(moved);
  return nextContacts;
}

interface WhatsAppContactsFormValues {
  preferredIndex: string;
}

const NO_ACTIVE_ENDPOINT_ERROR =
  "Nenhum Endpoint ativo encontrado para esta organização.";

function selectPhoneNumberIdFromContactPoint(
  contactPoint: fhir5.ContactPoint,
): string | null {
  const extensions = contactPoint.extension ?? [];
  const extensionMatch = extensions.find(
    (extension) =>
      extension.url === messagingConstants.PHONE_NUMBER_ID_EXTENSION_URL,
  );

  return typeof extensionMatch?.valueString === "string" &&
    extensionMatch.valueString.trim()
    ? extensionMatch.valueString.trim()
    : null;
}

const WhatsAppEndpointContactsPanel = memo(
  ({
    organizationId,
    wabaId,
    whatsappChannelOption,
    setupConfiguration,
    hasFluxZapExtensionActive = false,
    isFluxZapExtensionLoading = false,
    onFluxZapExtensionActivated,
    franchiseStatsRefreshSignal = 0,
    onFluxmedCheckoutCompleted,
    hasConfiguredAutomations = false,
    removeAllAutomationConfigs,
    persistedWabaId,
    onAfterChannelChange,
  }: WhatsAppEndpointContactsPanelProps) => {
    const isMetaChannel = whatsappChannelOption === "metaEmbeddedSignup";
    const isFluxmedChannel = whatsappChannelOption === "fluxmedDefault";

    const shouldLoadMetaContacts = isMetaChannel && Boolean(organizationId);
    const {
      endpointId,
      contacts: loadedContacts,
      isLoading,
      errorMessage,
      isSaving,
      saveReorderedContacts,
      refetch,
    } = useWhatsAppEndpointContacts(
      shouldLoadMetaContacts ? organizationId : undefined,
    );

    const isBlockingLoad =
      shouldLoadMetaContacts && isLoading && loadedContacts.length === 0;

    const hasContacts = loadedContacts.length > 0;
    const hasMultipleContacts = loadedContacts.length > 1;
    const inputsDisabled = isSaving || isLoading;

    const defaultPreferredIndex = useMemo(() => {
      if (loadedContacts.length === 0) return "0";
      const rankOneIndex = loadedContacts.findIndex((contactPoint) => {
        return typeof contactPoint.rank === "number" && contactPoint.rank === 1;
      });
      return String(rankOneIndex >= 0 ? rankOneIndex : 0);
    }, [loadedContacts]);

    const {
      control,
      reset,
      formState: { isDirty },
      watch,
    } = useForm<WhatsAppContactsFormValues>({
      defaultValues: { preferredIndex: defaultPreferredIndex },
    });

    useEffect(() => {
      reset({ preferredIndex: defaultPreferredIndex });
    }, [defaultPreferredIndex, reset]);

    const selectedRadioValue = watch("preferredIndex");

    const handleSave = useCallback(async () => {
      if (!endpointId) return;
      const selectedIndex = Number(selectedRadioValue);
      if (!Number.isFinite(selectedIndex)) return;

      const reorderedContacts = reorderContactsBySelectedIndex(
        loadedContacts,
        selectedIndex,
      );
      await saveReorderedContacts({
        endpointId,
        contacts: reorderedContacts.map((contactPoint, index) => ({
          ...contactPoint,
          rank: index + 1,
        })),
      });
      await refetch();
    }, [
      endpointId,
      loadedContacts,
      refetch,
      saveReorderedContacts,
      selectedRadioValue,
    ]);

    const showMetaMissingWabaAlert = isMetaChannel && !wabaId;

    const shouldShowNoActiveEndpointHint =
      isMetaChannel &&
      Boolean(organizationId) &&
      (errorMessage === NO_ACTIVE_ENDPOINT_ERROR ||
        errorMessage?.includes(NO_ACTIVE_ENDPOINT_ERROR));

    const shouldShowGenericError =
      Boolean(errorMessage) && !shouldShowNoActiveEndpointHint;

    const metaContactsSlot = isMetaChannel ? (
      <Stack spacing={1.25} sx={{ width: "100%" }}>
        {showMetaMissingWabaAlert ? (
          <Alert severity="warning">
            Defina o WABA (WhatsApp Business Account) para habilitar a lista de
            números no canal Meta.
          </Alert>
        ) : null}


        {shouldShowNoActiveEndpointHint ? (
          <Alert severity="info">
            Nenhum Endpoint ativo foi encontrado para esta organização. Conecte
            a Meta (Embedded Signup) e conclua o setup para habilitar a seleção
            do número preferencial.
          </Alert>
        ) : null}

        {shouldShowGenericError ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={refetch}
                sx={{ textTransform: "none" }}
              >
                Tentar novamente
              </Button>
            }
          >
            {errorMessage}
          </Alert>
        ) : null}

        {isBlockingLoad ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Carregando números do WhatsApp...
            </Typography>
          </Stack>
        ) : hasContacts ? (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="body1" fontWeight={500}>
                Números disponíveis
              </Typography>
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`${loadedContacts.length} número${loadedContacts.length === 1 ? "" : "s"}`}
              />
            </Stack>

            <Controller
              name="preferredIndex"
              control={control}
              render={({ field }) => {
                return (
                  <RadioGroup
                    aria-label="Número preferencial do WhatsApp"
                    name="whatsapp-preferred-number"
                    value={field.value}
                    onChange={(_, value) => field.onChange(value)}
                    sx={{ width: "100%" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      {loadedContacts.map((contactPoint, index) => {
                        const contactLabel =
                          (contactPoint.value ?? "").trim() ||
                          "Número não informado";
                        const phoneNumberId =
                          selectPhoneNumberIdFromContactPoint(contactPoint);
                        const isSelectable =
                          Boolean((contactPoint.value ?? "").trim()) &&
                          (contactPoint.system ?? "phone") === "phone";
                        const isSelected = field.value === String(index);

                        return (
                          <Box
                            key={`${index}-${contactPoint.system ?? ""}-${contactPoint.use ?? ""}-${contactPoint.value ?? ""}`}
                            sx={{
                              border: "1px solid",
                              borderColor: isSelected
                                ? "primary.main"
                                : "divider",
                              borderRadius: 3,
                              p: 1,
                              bgcolor: isSelected
                                ? "success100.main"
                                : "background.paper",
                              display: "inline-flex",
                              alignItems: "center",
                              width: "fit-content",
                              maxWidth: "100%",
                            }}
                          >
                            <FormControlLabel
                              value={String(index)}
                              disabled={inputsDisabled || !isSelectable}
                              control={
                                <Radio
                                  disableRipple
                                  sx={{
                                    p: 0.5,
                                    "& .MuiSvgIcon-root": { fontSize: 18 },
                                  }}
                                />
                              }
                              label={
                                <Stack
                                  direction="row"
                                  spacing={0.75}
                                  alignItems="center"
                                >
                                  <WhatsAppIcon
                                    sx={{ fontSize: 18, color: "#25D366" }}
                                  />
                                  <Typography
                                    variant="body2"
                                    fontWeight={isSelected ? 700 : 500}
                                    sx={{ wordBreak: "break-all" }}
                                  >
                                    {contactLabel}
                                    {!isSelectable ? " (indisponível)" : ""}
                                  </Typography>
                                </Stack>
                              }
                              sx={{ m: 0 }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  </RadioGroup>
                );
              }}
            />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ sm: "center" }}
            >
              {hasMultipleContacts ? (
                <>
                  <Button
                    variant="contained"
                    onClick={() => void handleSave()}
                    disabled={!endpointId || !isDirty || inputsDisabled}
                    sx={{ textTransform: "none" }}
                  >
                    {isSaving ? "Salvando..." : "Salvar alterações"}
                  </Button>
                  {!isDirty ? (
                    <Typography variant="caption" color="text.secondary">
                      Nenhuma alteração pendente.
                    </Typography>
                  ) : null}
                </>
              ) : null}
            </Stack>
          </Stack>
        ) : isMetaChannel &&
          !shouldShowNoActiveEndpointHint &&
          !shouldShowGenericError ? (
          <Alert severity="info">
            Nenhum número de WhatsApp foi encontrado no Endpoint ativo.
          </Alert>
        ) : null}
      </Stack>
    ) : undefined;

    if (!organizationId && !isFluxmedChannel) {
      return (
        <Stack spacing={2}>
          <WhatsAppConfigurationBillingActions
            organizationId={organizationId}
            channelOption={whatsappChannelOption}
            setupConfiguration={setupConfiguration}
            hasFluxZapExtensionActive={hasFluxZapExtensionActive}
            isFluxZapExtensionLoading={isFluxZapExtensionLoading}
            onFluxZapExtensionActivated={onFluxZapExtensionActivated}
            franchiseStatsRefreshSignal={franchiseStatsRefreshSignal}
            onFluxmedCheckoutCompleted={onFluxmedCheckoutCompleted}
            hasConfiguredAutomations={hasConfiguredAutomations}
            removeAllAutomationConfigs={removeAllAutomationConfigs}
            persistedWabaId={persistedWabaId}
            onAfterChannelChange={onAfterChannelChange}
          />
          <Alert severity="warning">
            Informe o organizationId para carregar o Endpoint do WhatsApp.
          </Alert>
        </Stack>
      );
    }

    return (
      <Stack spacing={2}>
        <WhatsAppConfigurationBillingActions
          organizationId={organizationId}
          channelOption={whatsappChannelOption}
          setupConfiguration={setupConfiguration}
          hasFluxZapExtensionActive={hasFluxZapExtensionActive}
          isFluxZapExtensionLoading={isFluxZapExtensionLoading}
          onFluxZapExtensionActivated={onFluxZapExtensionActivated}
          metaContactsSlot={metaContactsSlot}
          franchiseStatsRefreshSignal={franchiseStatsRefreshSignal}
          onFluxmedCheckoutCompleted={onFluxmedCheckoutCompleted}
          hasConfiguredAutomations={hasConfiguredAutomations}
          removeAllAutomationConfigs={removeAllAutomationConfigs}
          persistedWabaId={persistedWabaId}
          onAfterChannelChange={onAfterChannelChange}
        />
      </Stack>
    );
  },
);

WhatsAppEndpointContactsPanel.displayName = "WhatsAppEndpointContactsPanel";

export default WhatsAppEndpointContactsPanel;
